// Unified input handling for keyboard, mouse, and touch

export interface InputState {
  // Movement
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  sprint: boolean;

  // Combat
  attack: boolean;
  skill1: boolean;
  skill2: boolean;
  skill3: boolean;
  skill4: boolean;

  // Mouse
  mouseX: number;
  mouseY: number;
  mouseDeltaX: number;
  mouseDeltaY: number;
  mouseLeftDown: boolean;
  mouseRightDown: boolean;
  mouseMiddleDown: boolean;

  // Touch (for mobile)
  touchActive: boolean;
  touchX: number;
  touchY: number;
  joystickX: number;
  joystickY: number;
}

export interface InputConfig {
  canvas: HTMLCanvasElement;
  onPointerLockChange?: (locked: boolean) => void;
}

export class InputManager {
  private canvas: HTMLCanvasElement;
  private state: InputState;
  private keyMap: Map<string, keyof InputState> = new Map();
  private pointerLocked: boolean = false;
  private onPointerLockChange?: (locked: boolean) => void;

  // Touch joystick state
  private joystickTouchId: number | null = null;
  private joystickOrigin: { x: number; y: number } = { x: 0, y: 0 };

  constructor(config: InputConfig) {
    this.canvas = config.canvas;
    this.onPointerLockChange = config.onPointerLockChange;

    // Initialize state
    this.state = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      jump: false,
      sprint: false,
      attack: false,
      skill1: false,
      skill2: false,
      skill3: false,
      skill4: false,
      mouseX: 0,
      mouseY: 0,
      mouseDeltaX: 0,
      mouseDeltaY: 0,
      mouseLeftDown: false,
      mouseRightDown: false,
      mouseMiddleDown: false,
      touchActive: false,
      touchX: 0,
      touchY: 0,
      joystickX: 0,
      joystickY: 0,
    };

    // Setup key mappings
    this.setupKeyMappings();

    // Bind event listeners
    this.bindEvents();
  }

  private setupKeyMappings(): void {
    // WASD movement
    this.keyMap.set('KeyW', 'forward');
    this.keyMap.set('KeyS', 'backward');
    this.keyMap.set('KeyA', 'left');
    this.keyMap.set('KeyD', 'right');

    // Arrow keys alternative
    this.keyMap.set('ArrowUp', 'forward');
    this.keyMap.set('ArrowDown', 'backward');
    this.keyMap.set('ArrowLeft', 'left');
    this.keyMap.set('ArrowRight', 'right');

    // Actions
    this.keyMap.set('Space', 'jump');
    this.keyMap.set('ShiftLeft', 'sprint');
    this.keyMap.set('ShiftRight', 'sprint');

    // Skills (1-4 keys)
    this.keyMap.set('Digit1', 'skill1');
    this.keyMap.set('Digit2', 'skill2');
    this.keyMap.set('Digit3', 'skill3');
    this.keyMap.set('Digit4', 'skill4');
  }

  private bindEvents(): void {
    // Keyboard events
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));

    // Mouse events
    this.canvas.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.canvas.addEventListener('wheel', this.onWheel.bind(this));
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Pointer lock events
    document.addEventListener('pointerlockchange', this.onPointerLockChangeEvent.bind(this));

    // Touch events (mobile)
    this.canvas.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
    this.canvas.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    this.canvas.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });

    // Prevent default touch behaviors
    this.canvas.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
  }

  private onKeyDown(event: KeyboardEvent): void {
    // Ignore if typing in input field
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    const action = this.keyMap.get(event.code);
    if (action && typeof this.state[action] === 'boolean') {
      (this.state[action] as boolean) = true;
    }
  }

  private onKeyUp(event: KeyboardEvent): void {
    const action = this.keyMap.get(event.code);
    if (action && typeof this.state[action] === 'boolean') {
      (this.state[action] as boolean) = false;
    }
  }

  private onMouseDown(event: MouseEvent): void {
    switch (event.button) {
      case 0: // Left click - camera rotation
        this.state.mouseLeftDown = true;
        // Request pointer lock for camera control
        if (!this.pointerLocked) {
          this.canvas.requestPointerLock();
        }
        break;
      case 1: // Middle click
        this.state.mouseMiddleDown = true;
        break;
      case 2: // Right click - movement/interaction
        this.state.mouseRightDown = true;
        break;
    }
  }

  private onMouseUp(event: MouseEvent): void {
    switch (event.button) {
      case 0:
        this.state.mouseLeftDown = false;
        // Release pointer lock
        if (this.pointerLocked) {
          document.exitPointerLock();
        }
        break;
      case 1:
        this.state.mouseMiddleDown = false;
        break;
      case 2:
        this.state.mouseRightDown = false;
        break;
    }
  }

  private onMouseMove(event: MouseEvent): void {
    this.state.mouseX = event.clientX;
    this.state.mouseY = event.clientY;

    if (this.pointerLocked) {
      this.state.mouseDeltaX = event.movementX;
      this.state.mouseDeltaY = event.movementY;
    } else {
      this.state.mouseDeltaX = 0;
      this.state.mouseDeltaY = 0;
    }
  }

  private onWheel(_event: WheelEvent): void {
    // Will be used for camera zoom
  }

  private onPointerLockChangeEvent(): void {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    this.onPointerLockChange?.(this.pointerLocked);
  }

  private onTouchStart(event: TouchEvent): void {
    event.preventDefault();

    for (let i = 0; i < event.changedTouches.length; i++) {
      const touch = event.changedTouches[i];
      const x = touch.clientX;
      const y = touch.clientY;

      // Left side of screen = joystick
      if (x < window.innerWidth / 2 && this.joystickTouchId === null) {
        this.joystickTouchId = touch.identifier;
        this.joystickOrigin = { x, y };
        this.state.touchActive = true;
      } else {
        // Right side = attack
        this.state.attack = true;
      }
    }
  }

  private onTouchMove(event: TouchEvent): void {
    event.preventDefault();

    for (let i = 0; i < event.changedTouches.length; i++) {
      const touch = event.changedTouches[i];

      if (touch.identifier === this.joystickTouchId) {
        const dx = touch.clientX - this.joystickOrigin.x;
        const dy = touch.clientY - this.joystickOrigin.y;
        const maxDistance = 50;

        // Normalize joystick values (-1 to 1)
        this.state.joystickX = Math.max(-1, Math.min(1, dx / maxDistance));
        this.state.joystickY = Math.max(-1, Math.min(1, dy / maxDistance));

        // Update virtual button states based on joystick
        this.state.forward = this.state.joystickY < -0.3;
        this.state.backward = this.state.joystickY > 0.3;
        this.state.left = this.state.joystickX < -0.3;
        this.state.right = this.state.joystickX > 0.3;
      }

      this.state.touchX = touch.clientX;
      this.state.touchY = touch.clientY;
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    for (let i = 0; i < event.changedTouches.length; i++) {
      const touch = event.changedTouches[i];

      if (touch.identifier === this.joystickTouchId) {
        this.joystickTouchId = null;
        this.state.joystickX = 0;
        this.state.joystickY = 0;
        this.state.forward = false;
        this.state.backward = false;
        this.state.left = false;
        this.state.right = false;
        this.state.touchActive = false;
      } else {
        this.state.attack = false;
      }
    }
  }

  // Get current input state (read-only copy)
  public getState(): Readonly<InputState> {
    return { ...this.state };
  }

  // Reset per-frame deltas (call at end of frame)
  public resetDeltas(): void {
    this.state.mouseDeltaX = 0;
    this.state.mouseDeltaY = 0;
  }

  // Check if any movement input is active
  public isMoving(): boolean {
    return this.state.forward || this.state.backward || this.state.left || this.state.right;
  }

  // Get movement vector (normalized)
  public getMovementVector(): { x: number; z: number } {
    let x = 0;
    let z = 0;

    if (this.state.forward) z -= 1;
    if (this.state.backward) z += 1;
    if (this.state.left) x -= 1;
    if (this.state.right) x += 1;

    // Normalize
    const length = Math.sqrt(x * x + z * z);
    if (length > 0) {
      x /= length;
      z /= length;
    }

    return { x, z };
  }

  public isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  public destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown.bind(this));
    window.removeEventListener('keyup', this.onKeyUp.bind(this));
    // Note: Should store bound function references for proper cleanup
  }
}
